/**
 * 管理后台路由配置
 * @description 后台（/v2/admin）路由
 * @update 2025-12-25 重构为嵌套结构，使用 AdminLayout 作为父布局
 * @update 2025-12-31 补充所有子路由的 permissions 配置，实现细粒度权限控制
 */

import type { RouteRecordRaw } from 'vue-router'
import { ADMIN_RBAC_PERMISSIONS } from '@/config/admin/rbac'
import { UserRole } from '@/types/router'

import { adminFinanceRoutes } from './admin/adminFinanceRoutes'
import { adminOpsRoutes } from './admin/adminOpsRoutes'
// 导入拆分后的路由模块
import { adminReviewRoutes } from './admin/adminReviewRoutes'
import { adminRiskRoutes } from './admin/adminRiskRoutes'
import { adminSystemRoutes } from './admin/adminSystemRoutes'

/**
 * Admin 布局组件
 */
const AdminLayout = () => import('@/components/admin/layouts/AdminLayout/AdminLayout.vue')

/**
 * Admin 子路由定义
 * 所有子路由都在 AdminLayout 内渲染
 */
const adminChildRoutes: RouteRecordRaw[] = [
  // ─────────────────────────────────────────────────
  // 首页
  // ─────────────────────────────────────────────────
  {
    path: 'dashboard',
    name: 'AdminDashboard',
    component: () => import('@/views/admin/AdminDashboardPage.vue'),
    meta: {
      title: '首页',
      sidebarId: 'dashboard',
      permissions: [ADMIN_RBAC_PERMISSIONS.DASHBOARD_VIEW],
      breadcrumb: true,
      cache: true,
    },
  },

  ...adminReviewRoutes,
  ...adminFinanceRoutes,
  ...adminRiskRoutes,
  ...adminOpsRoutes,
  ...adminSystemRoutes,

  // ─────────────────────────────────────────────────
  // 个人中心
  // ─────────────────────────────────────────────────
  {
    path: 'profile',
    name: 'AdminProfile',
    component: () => import('@/views/admin/AdminProfilePage.vue'),
    meta: {
      title: '个人中心',
      sidebarId: '',
      permissions: [ADMIN_RBAC_PERMISSIONS.DASHBOARD_VIEW],
      breadcrumb: true,
    },
  },

  // ─────────────────────────────────────────────────
  // Admin 内部 404 兜底
  // ─────────────────────────────────────────────────
  {
    path: ':pathMatch(.*)*',
    name: 'AdminNotFound',
    component: () => import('@/views/admin/common/AdminNotFoundPage.vue'),
    meta: {
      title: '页面未找到',
      breadcrumb: false,
    },
  },
]

/**
 * Admin 路由配置
 */
const adminRoutes: RouteRecordRaw[] = [
  // 登录页（不使用 AdminLayout）
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/admin/AdminLoginPage.vue'),
    meta: {
      title: '后台登录',
      requiresAuth: false,
      breadcrumb: false,
    },
  },

  // 403 无权限页面（不使用 AdminLayout）
  {
    path: '/admin/403',
    name: 'AdminForbidden',
    component: () => import('@/views/admin/common/AdminForbiddenPage.vue'),
    meta: {
      title: '无权限',
      requiresAuth: false,
      breadcrumb: false,
    },
  },

  // 500 服务器错误页面（不使用 AdminLayout）
  {
    path: '/admin/500',
    name: 'AdminServerError',
    component: () => import('@/views/admin/common/AdminServerErrorPage.vue'),
    meta: {
      title: '服务器错误',
      requiresAuth: false,
      breadcrumb: false,
    },
  },

  // Admin 主布局（使用 AdminLayout）
  {
    path: '/admin',
    name: 'AdminRoot',
    component: AdminLayout,
    redirect: '/admin/dashboard',
    meta: {
      title: '管理后台',
      requiresAuth: true,
      roles: [UserRole.ADMIN],
    },
    children: adminChildRoutes,
  },
]

export default adminRoutes
